/**
 * CEVAP DEĞERLENDİRME - STANDALONE MODÜL (JavaScript)
 * ====================================================
 * Tekrara değerlendirme (cevap anahtarı düzeltmesi sonrası puan yenileme) için.
 * Sınav uygulamasındaki puanlama ile BİRE BİR AYNI mantık (split, trim, clearText, benzerlik, formül).
 *
 * Bağımlılık: npm install string-similarity
 *
 * TEK FONKSİYON KULLANIMI:
 *   import { computeScore } from './answer-evaluation-standalone.js';
 *   const yeniPuan = computeScore(ogrenciCevabi, dogruCevap, maxPuan);
 *
 * - dogruCevap: Doğru cevap metni. Alternatifler ENTER (newline) ile ayrılır, örn. "she\nhe"
 * - Parçalama, trim, clearText, benzerlik ve puan hesabı hepsi bu fonksiyon içinde yapılır.
 * - Dönen değer: sadece hesaplanan puan (number).
 */

// React/Webpack/Vite: import kullanın. Sadece Node (CommonJS) kullanıyorsanız require ile değiştirin.
import stringSimilarity from "string-similarity";

// Uygulama config'i ile aynı (api.similarityThreshold: 60). Şu an sadece split/clearText/compareTwoStrings kullanılıyor.
const DEFAULT_ENHANCED_THRESHOLD = 60;

// =============================================================================
// TEXT UTILS - clearText (puanlama/benzerlik için metin normalizasyonu)
// =============================================================================

function numberToWords(num) {
  if (num === 0) return "zero";
  if (num < 0) return "negative " + numberToWords(-num);

  const ones = [
    "", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
    "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen",
    "seventeen", "eighteen", "nineteen",
  ];
  const tens = [
    "", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety",
  ];

  if (num < 20) return ones[num];
  if (num < 100) {
    const ten = Math.floor(num / 10);
    const one = num % 10;
    return tens[ten] + (one > 0 ? " " + ones[one] : "");
  }
  if (num < 1000) {
    const hundred = Math.floor(num / 100);
    const remainder = num % 100;
    return ones[hundred] + " hundred" + (remainder > 0 ? " " + numberToWords(remainder) : "");
  }
  return String(num);
}

/**
 * Metni benzerlik karşılaştırması için temizler (küçük harf, sayı→yazı, kısaltmalar, özel karakterler).
 */
function clearText(readingText) {
  if (!readingText) return "";

  return String(readingText)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b(\d+)\b/g, (match, numStr) => {
      const num = parseInt(numStr, 10);
      if (num >= 0 && num < 1000) return numberToWords(num);
      return match;
    })
    .replace(/\bdr\.?\b/g, "doctor")
    .replace(/\bmr\.?\b/g, "mister")
    .replace(/\bmrs\.?\b/g, "missus")
    .replace(/\bms\.?\b/g, "miss")
    .replace(/\bprof\.?\b/g, "professor")
    .replace(/\bst\.?\b/g, "saint")
    .replace(/\b(can't|cannot)\b/g, "can not")
    .replace(/\b(i'm|im)\b/g, "i am")
    .replace(/\b(it's|its)\b/g, "it is")
    .replace(/\b(you're|youre)\b/g, "you are")
    .replace(/\b(we're|were)\b/g, "we are")
    .replace(/\b(they're|theyre)\b/g, "they are")
    .replace(/\b(he's|hes)\b/g, "he is")
    .replace(/\b(she's|shes)\b/g, "she is")
    .replace(/\b(that's|thats)\b/g, "that is")
    .replace(/\b(what's|whats)\b/g, "what is")
    .replace(/\b(who's|whos)\b/g, "who is")
    .replace(/\b(where's|wheres)\b/g, "where is")
    .replace(/\b(here's|heres)\b/g, "here is")
    .replace(/\b(there's|theres)\b/g, "there is")
    .replace(/\b(let's|lets)\b/g, "let us")
    .replace(/\b(don't|dont)\b/g, "do not")
    .replace(/\b(doesn't|doesnt)\b/g, "does not")
    .replace(/\b(didn't|didnt)\b/g, "did not")
    .replace(/\b(won't|wont)\b/g, "will not")
    .replace(/\b(wouldn't|wouldnt)\b/g, "would not")
    .replace(/\b(shouldn't|shouldnt)\b/g, "should not")
    .replace(/\b(couldn't|couldnt)\b/g, "could not")
    .replace(/\b(isn't|isnt)\b/g, "is not")
    .replace(/\b(aren't|arent)\b/g, "are not")
    .replace(/\b(wasn't|wasnt)\b/g, "was not")
    .replace(/\b(weren't|werent)\b/g, "are not")
    .replace(/\b(hasn't|hasnt)\b/g, "has not")
    .replace(/\b(haven't|havent)\b/g, "have not")
    .replace(/\b(hadn't|hadnt)\b/g, "had not")
    .replace(/[^\w\s]/g, "")
    .trim();
}

// =============================================================================
// SCORING - Benzerlik ve puan hesaplama
// =============================================================================

// =============================================================================
// TEK FONKSİYON: Doğru cevap + öğrenci cevabı + max puan → sadece yeni puan
// Sınav uygulamasındaki akışla bire bir aynı (Enter ile parçalama, trim, clearText, benzerlik, formül)
// =============================================================================

/**
 * Tekrara değerlendirme için tek fonksiyon.
 * Doğru cevap string + öğrenci cevabı string + max puan ver; sadece yeni puanı al.
 *
 * @param studentAnswer - Öğrenci cevabı (STT'den gelen veya kayıtlı metin)
 * @param correctAnswer - Doğru cevap. Alternatifler ENTER ile ayrılır: "she" veya "she\nhe"
 * @param maxScore - Bu soru/part için maksimum puan (örn. 10)
 * @returns Hesaplanan puan (number). 0 ile maxScore arası.
 */
function computeScore(studentAnswer, correctAnswer, maxScore) {
  const max = Number(maxScore);
  if (max <= 0 || !correctAnswer || String(correctAnswer).trim() === "") {
    return 0;
  }

  // Doğru cevabı Enter'a göre parçala (sınav uygulaması ile aynı: \n, \r\n, \r)
  const alternativeAnswers = String(correctAnswer)
    .split(/\r?\n|\r/)
    .map((answer) =>
      answer.replace(/\r/g, "").replace(/\n/g, "").replace(/\t/g, " ").trim()
    )
    .filter((answer) => answer.length > 0);

  if (alternativeAnswers.length === 0) return 0;

  const text = studentAnswer != null ? String(studentAnswer).trim() : "";
  const cleanedText = clearText(text);

  let maxSimilarity = 0;
  for (const answer of alternativeAnswers) {
    const cleanedAnswer = clearText(answer);
    const sim = stringSimilarity.compareTwoStrings(cleanedText, cleanedAnswer);
    if (sim > maxSimilarity) maxSimilarity = sim;
  }

  const similarityPercent = Math.round(maxSimilarity * 100);
  const studentScore = (similarityPercent * max) / 100;
  return studentScore;
}

// ES module export – tek fonksiyon kullan: import { computeScore } from '...'
export { computeScore, clearText, DEFAULT_ENHANCED_THRESHOLD };
